import { FieldSummary, SearchFilters } from '@/types/field';
import { ArenasApiResponse } from '@/types/arena';
import api from '@/services/apiService';

class FieldService {
  private fields: FieldSummary[] = [];
  private isLoading: boolean = false;
  private isLoaded: boolean = false;
  private loadingPromise: Promise<void> | null = null;
  private currentPage: number = 0;
  private perPage: number = 6;
  private totalPages: number = 1;
  private total: number = 0;
  private currentFilters: SearchFilters | null = null;

  constructor() {
    this.loadFields();
  }

  // Dynamically fetch fields data from public directory
  private async loadFields() {
    if (this.isLoading || this.isLoaded) return;

    this.isLoading = true;
    this.loadingPromise = (async () => {
      try {
        await this.fetchPage(1, null);
        this.isLoaded = true;
      } catch (error) {
        console.error(error);
        this.fields = [];
      } finally {
        this.isLoading = false;
        this.loadingPromise = null;
      }
    })();

    await this.loadingPromise;
  }

  // Build query string for arenas endpoint from page and optional filters
  private buildQuery(page: number, filters?: SearchFilters) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(this.perPage));
    if (filters) {
      if (filters.location) params.set('location', filters.location);
      if (filters.kind) params.set('kind', filters.kind);
      if (filters.query) params.set('query', filters.query);
      if (filters.priceMin !== undefined) params.set('price_min', String(filters.priceMin));
      if (filters.priceMax !== undefined) params.set('price_max', String(filters.priceMax));
      if (filters.date) params.set('date', filters.date);
      if (filters.time) params.set('time', filters.time);
    }
    return params.toString();
  }

  // Fetch a specific page from API and append/replace fields accordingly
  async fetchPage(page: number, filters?: SearchFilters | null): Promise<FieldSummary[]> {
    // store current filters
    this.currentFilters = filters || null;

    const qs = this.buildQuery(page, filters || undefined);
    const res = await api.get(`/arenas?${qs}`);
    if (res.status < 200 || res.status >= 300) throw new Error('Failed to fetch arenas');
    const body: ArenasApiResponse = res.data;
    const arenas: FieldSummary[] = body.data?.arenas || [];

    // If fetching first page, replace existing fields, else append
    if (page === 1) {
      this.fields = arenas;
    } else {
      this.fields = [...this.fields, ...arenas];
    }

    // Update pagination info if present
    const pagination = body.data?.pagination;
    if (pagination) {
      this.currentPage = pagination.current_page || page;
      this.perPage = pagination.per_page || this.perPage;
      this.total = pagination.total || this.total;
      this.totalPages = pagination.total_pages || this.totalPages;
    } else {
      // fallback: if we got fewer items than perPage, assume last page
      this.currentPage = page;
      if (arenas.length < this.perPage) {
        this.totalPages = page;
      }
    }

    return arenas;
  }

  async fetchNextPage(): Promise<FieldSummary[]> {
    const nextPage = this.currentPage + 1;
    if (this.currentPage >= this.totalPages) return [];
    return this.fetchPage(nextPage, this.currentFilters || undefined);
  }

  async resetAndFetch(filters?: SearchFilters | null): Promise<FieldSummary[]> {
    this.fields = [];
    this.currentPage = 0;
    this.totalPages = 1;
    this.total = 0;
    this.currentFilters = filters || null;
    return this.fetchPage(1, filters || undefined);
  }

  hasMore(): boolean {
    return this.currentPage < this.totalPages;
  }

  // Ensure data is loaded before using
  async ensureDataLoaded(): Promise<void> {
    if (this.isLoaded) return;
    if (this.loadingPromise) {
      await this.loadingPromise;
      return;
    }
    await this.loadFields();
  }

  // Get all fields
  async getAllFields(): Promise<FieldSummary[]> {
    await this.ensureDataLoaded();
    return this.fields;
  }

  // Get field by ID
  async getFieldById(slug: string | number): Promise<FieldSummary | null> {
    await this.ensureDataLoaded();
    const field = this.fields.find((f) => f.slug.toString() === slug);
    return field || null;
  }

  async getFieldByUrl(url: string): Promise<FieldSummary | null> {
    await this.ensureDataLoaded();
    const field = this.fields.find((f) => f.slug === url);
    return field || null;
  }

  // Search fields with filters
  async searchFields(filters: SearchFilters): Promise<FieldSummary[]> {
    // For filter/search, reset current fields and fetch first page with filters
    await this.resetAndFetch(filters || null);
    return this.fields;
  }

  // Get unique locations for filter options
  getUniqueLocations(): string[] {
    const locations = this.fields.map((field) => field?.maps?.city || '').filter((loc) => loc);
    return [...new Set(locations)].sort();
  }

  // Get unique sport types for filter options
  getUniqueSportTypes(): string[] {
    const types = this.fields.flatMap((field) => field.sport_categories?.categories || []);
    return [...new Set(types)].sort();
  }

  // Get featured/recommended fields (top rated)
  async getFeaturedFields(limit: number = 6): Promise<FieldSummary[]> {
    await this.ensureDataLoaded();
    const featured = this.fields.slice(0, limit);
    return featured.map((field) => ({
      slug: field.slug,
      name: field.name,
      description: field.description,
      email: field?.email || '',
      sport_categories: field.sport_categories,
      facilities: field.facilities || { data: [] },
      images: field?.images,
      maps: {
        google_maps_url: field.maps?.google_maps_url,
        address: field?.maps?.address || '',
        city: field?.maps?.city || '',
        kecamatan: field?.maps?.kecamatan || '',
      },
      min_price: field?.min_price || 0,
    }));
  }

  // Fetch featured fields directly from API without mutating internal state
  async getPublicFeaturedFields(limit: number = 6): Promise<FieldSummary[]> {
    try {
      const res = await api.get(`/arenas?page=1&limit=${limit}`);
      const body: ArenasApiResponse = res.data;
      const arenas: FieldSummary[] = body.data?.arenas || [];

      // Map to same response shape as getFeaturedFields
      return arenas.map((field) => ({
        slug: field.slug,
        name: field.name,
        description: field.description,
        email: field.email,
        sport_categories: field.sport_categories,
        facilities: field.facilities || { data: [] },
        images: field.images || { images: [] },
        maps: field.maps || { google_maps_url: '', address: '', city: '' },
        min_price: field.min_price || 0,
      }));
    } catch (error) {
      console.error('Failed to fetch public featured fields:', error);
      return [];
    }
  }

  // Fetch full detail for a specific arena including physical fields & time slots
  async getArenaDetail(slug: string, date?: string): Promise<any> {
    try {
      const q = new URLSearchParams();
      if (date) q.set('date', date);
      const queryString = date ? `?${q.toString()}` : '';
      const res = await api.get(`/arenas/detail/${slug}${queryString}`);
      return res.data?.data || res.data;
    } catch (err) {
      console.error('Failed to fetch arena detail:', err);
      throw err;
    }
  }
}

// Export singleton instance
export const fieldService = new FieldService();
export default fieldService;
